import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Snackbar,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "store";
import DecimalField from "../shared/DecimalField";
import VatPriceField from "../shared/VatPriceField";
import { formatPrice, formatPriceWithCurrency } from "../../utils/vat";

const SETTINGS_URL = "/api/quote-machine/settings/";

// "Tarjousautomaatti" sub-tab of the Ominaisuudet (Features) settings tab, shown to
// organisations with the quote_machine_enabled entitlement. Every setting may be left empty:
// the quote then follows the workshop's own invoices, and the page says what that gives.
function FeaturesQuoteMachineTab({ onDirtyChange }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const toForm = (payload) => {
    const withVat = payload.hourly_rate_entered_with_vat;
    return {
      hourly: (withVat ? payload.hourly_rate_incl_vat : payload.hourly_rate) ?? "",
      withVat,
      labour_level: payload.labour_level ?? "",
      parts_level: payload.parts_level ?? "",
      margin_percent: parseFloat(payload.margin_percent) ? payload.margin_percent : "",
    };
  };

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    axios
      .get(SETTINGS_URL)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setForm(toForm(res.data));
        setIsDirty(false);
      })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [currentOrganization?.organization_id]);

  const change = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      setSaving(true);
      const res = await axios.patch(SETTINGS_URL, {
        hourly_rate: form.withVat ? "" : form.hourly,
        hourly_rate_incl_vat: form.withVat ? form.hourly : "",
        hourly_rate_entered_with_vat: form.withVat,
        labour_level: form.labour_level,
        parts_level: form.parts_level,
        margin_percent: form.margin_percent,
      });
      setData(res.data);
      setForm(toForm(res.data));
      setIsDirty(false);
      setSnackbar({ open: true, message: t("quoteMachine.settings.saved"), severity: "success" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t("quoteMachine.settings.saveFailed"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Report dirty state to parent (sub-tab + tab-switch guards).
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]);

  if (loadError) {
    return <Alert severity="error">{t("quoteMachine.settings.loadFailed")}</Alert>;
  }
  if (!form || !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { suggestions, effective } = data;
  const suggestedHourly = form.withVat ? suggestions.hourly_rate_incl_vat : suggestions.hourly_rate;
  const effectiveHourly = form.withVat ? effective.hourly_rate_incl_vat : effective.hourly_rate;

  const suggestion = (value, label, onUse, testId) => (
    value === null || value === undefined ? null : (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Button size="small" onClick={onUse} data-testid={testId} sx={{ textTransform: "none" }}>
          {t("quoteMachine.settings.useSuggestion")}
        </Button>
      </Box>
    )
  );

  const levelField = (field, suggested) => (
    <>
      <DecimalField
        size="small"
        label={t(`quoteMachine.settings.${field}`)}
        value={form[field]}
        onChange={change(field)}
        max={2.5}
        placeholder={t("quoteMachine.settings.automatic")}
        InputLabelProps={{ shrink: true }}
        inputProps={{ "data-testid": `quote-machine-${field}` }}
        sx={{ width: 220 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t(`quoteMachine.settings.${field}_help`)}
      </Typography>
      {suggestion(
        suggested,
        t("quoteMachine.settings.fromInvoices", { value: formatPrice(suggested) }),
        () => change(field)(String(suggested)),
        `quote-machine-${field}-use`
      )}
    </>
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("quoteMachine.settings.intro")}
      </Typography>
      {!data.has_price_book && (
        <Alert severity="info" sx={{ mb: 2 }}>{t("quoteMachine.noPriceBook")}</Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <VatPriceField
            value={form.hourly}
            onChange={change("hourly")}
            withVat={form.withVat}
            onModeChange={(newWithVat) => { setForm((prev) => ({ ...prev, withVat: newWithVat })); setIsDirty(true); }}
            vatRate={parseFloat(data.vat_rate)}
            labelInclVat={t("quoteMachine.settings.hourlyRateGross")}
            labelExclVat={t("quoteMachine.settings.hourlyRateNet")}
            inputProps={{ "data-testid": "quote-machine-hourly-rate" }}
            sx={{ maxWidth: 340 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("quoteMachine.settings.hourlyRateHelp")}
          </Typography>
          {form.hourly === "" && effectiveHourly && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t(`quoteMachine.settings.effective_${effective.hourly_source}`, { amount: formatPriceWithCurrency(effectiveHourly) })}
            </Typography>
          )}
          {suggestion(
            suggestedHourly,
            t("quoteMachine.settings.hourlyFromInvoices", { amount: formatPriceWithCurrency(suggestedHourly) }),
            () => change("hourly")(String(suggestedHourly)),
            "quote-machine-hourly-use"
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <DecimalField
            size="small"
            label={t("quoteMachine.settings.margin")}
            value={form.margin_percent}
            onChange={change("margin_percent")}
            max={50}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ "data-testid": "quote-machine-margin" }}
            sx={{ width: 220 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("quoteMachine.settings.marginHelp")}
          </Typography>
          {data.within_quote_plus_15 !== null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("quoteMachine.settings.within15", { percent: data.within_quote_plus_15 })}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>{levelField("labour_level", suggestions.labour_level)}</Grid>
        <Grid item xs={12} md={6}>{levelField("parts_level", suggestions.parts_level)}</Grid>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || !isDirty}
              data-testid="quote-machine-settings-save"
            >
              {saving ? <CircularProgress size={20} /> : <span>{t("Save Changes")}</span>}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FeaturesQuoteMachineTab;
